<script lang="ts" setup>
import { SITEURL } from "@libTs/cms.globals";
import axios from "axios";
import { computed, inject, PropType, ref } from "vue";
import { TranslationService } from "../services/translation.service";
import { OverlaysService } from "../services/overlays.service";
import { EOverlay } from "../EOverlay";
import { vInputText as vInputTextDir } from "../directives/v-input-text";

const vInputText = vInputTextDir;

const t = inject<TranslationService>("translationSvc")!.t;
const overlaysSvc = inject<OverlaysService>("overlaysSvc")!;

interface SourcingFormModel {
    brand: string;
    productname: string;
    catno: string;
    casno: string;
    purity: string;
    quantity: string;
    packsize: string;
    isnewcustomer: boolean;
    customernumber: string;
    company: string;
    department: string;
    street: string;
    zip: string;
    city: string;
    fullname: string;
    email: string;
    phone: string;
    message: string;
}

const requiredParams = [
    "productname",
    "catno",
    "quantity",
    "packsize",
    "company",
    "street",
    "zip",
    "city",
    "fullname",
    "email",
];

const { initUserModel, formTitle, isPartner } = defineProps({
    initUserModel: {
        type: Object as PropType<Partial<SourcingFormModel>>,
        required: true,
    },
    formTitle: {
        type: String,
        required: true,
    },
    isPartner: {
        type: Boolean,
        required: true,
    },
});

const isSending = ref<boolean>(false);
const sent = ref<boolean>(false);

// Initial state of the model
const initialModelState: SourcingFormModel = {
    brand: "",
    productname: "",
    catno: "",
    casno: "",
    purity: "",
    quantity: "",
    packsize: "",
    isnewcustomer: false,
    customernumber: "",
    company: "",
    department: "",
    street: "",
    zip: "",
    city: "",
    fullname: "",
    email: "",
    phone: "",
    message: "",
};

const model = ref<SourcingFormModel>({ ...initialModelState });

const initModelUser = (modelParam: Partial<SourcingFormModel>): void => {
    if (typeof modelParam === "object" && modelParam) {
        Object.assign(model.value, modelParam);
    }
};

initModelUser(initUserModel);

const isPristine = computed(() => {
    return Object.keys(model.value).every((key) => {
        return (
            model.value[key as keyof SourcingFormModel] ===
            initialModelState[key as keyof SourcingFormModel]
        );
    });
});

const isInvalid = computed(() => {
    const alwaysRequired = requiredParams.some((key) => {
        return !model.value[key as keyof SourcingFormModel];
    });

    const onlyIfExistingCustomer = model.value.isnewcustomer
        ? false
        : !model.value.customernumber;

    return alwaysRequired || onlyIfExistingCustomer;
});

const submit = (): void => {
    if (isPristine.value || isInvalid.value || isSending.value || sent.value) {
        return;
    }

    // make sure that the customer number is empty
    // for new customers
    if (model.value.isnewcustomer) {
        model.value.customernumber = "";
    }

    isSending.value = true;
    sent.value = false;
    axios
        .post(`${SITEURL}actions/sapShop/thp/upload`, model.value, {
            responseType: "json",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
        })
        .then((data) => {
            sent.value = true;
            // console.log("SourcingController.submit, response:", data);
            alert(t("THP_FORM_SENT"));
        })
        .catch((e) => {
            console.error("SourcingController.submit, error:", e);
        })
        .finally(() => {
            isSending.value = false;
        });
};
</script>

<template>
    <form
        name="form"
        class="sourcing-form"
        @submit.prevent="submit()"
        novalidate
    >
        <h2 class="h3 mb-10">{{ formTitle }}</h2>

        <!------Produktangaben------>

        <div class="input-text">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="brand"
                v-model="model.brand"
                v-input-text
                :placeholder="t('THP_FORM_BRAND')"
            />
        </div>
        <div class="input-text mt-5">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="productname"
                v-model="model.productname"
                :placeholder="`${t('THP_FORM_PRODUCTNAME')} *`"
                v-input-text
                required
            />
        </div>
        <div class="input-text mt-5">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="catno"
                v-model="model.catno"
                :placeholder="`${t('THP_FORM_CATNO')} *`"
                v-input-text
                required
            />
        </div>
        <div class="input-text mt-5">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="casno"
                v-model="model.casno"
                v-input-text
                :placeholder="t('THP_FORM_CASNO')"
            />
        </div>
        <div class="input-text mt-5">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="purity"
                v-model="model.purity"
                v-input-text
                :placeholder="t('THP_FORM_PURITY')"
            />
        </div>
        <div class="input-text mt-5">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="quantity"
                v-model="model.quantity"
                :placeholder="`${t('THP_FORM_QUANTITY')} *`"
                v-input-text
                required
            />
        </div>
        <div class="input-text mt-5">
            <input
                class="input-text-input input-text-input-large"
                type="text"
                name="packsize"
                v-model="model.packsize"
                :placeholder="`${t('THP_FORM_PACKSIZE')} *`"
                v-input-text
                required
            />
        </div>

        <!------Kontaktangaben------>

        <div class="pt-15"></div>

        <div v-if="!isPartner">
            <div class="input-checkbox">
                <input
                    class="input-checkbox-input input-checkbox-input-large"
                    id="isnewcustomer"
                    type="checkbox"
                    name="isnewcustomer"
                    v-model="model.isnewcustomer"
                    :required="!model.customernumber"
                />
                <label class="input-checkbox-label" for="isnewcustomer">{{
                    t("THP_FORM_NEWCUSTOMER")
                }}</label>
            </div>
            <div class="mt-5" v-if="!model.isnewcustomer">
                <div
                    class="btn-label"
                    @click="overlaysSvc.showOverlay(EOverlay.loginForm)"
                >
                    {{ t("LOGIN") }}
                </div>
            </div>
        </div>

        <div v-if="isPartner || model.isnewcustomer">
            <div class="input-text mt-5" v-if="!model.isnewcustomer">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="customernumber"
                    v-model="model.customernumber"
                    :placeholder="`${t('THP_FORM_CUSTOMER_NUMBER')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="company"
                    v-model="model.company"
                    :placeholder="`${t('THP_FORM_COMPANY')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="department"
                    v-model="model.department"
                    :placeholder="t('THP_FORM_DEPARTMENT')"
                    v-input-text
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="street"
                    v-model="model.street"
                    :placeholder="`${t('THP_FORM_STREET')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="zip"
                    v-model="model.zip"
                    :placeholder="`${t('THP_FORM_ZIP')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="city"
                    v-model="model.city"
                    :placeholder="`${t('THP_FORM_CITY')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="fullname"
                    v-model="model.fullname"
                    :placeholder="`${t('THP_FORM_FULLNAME')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="email"
                    v-model="model.email"
                    :placeholder="`${t('THP_FORM_EMAIL')} *`"
                    v-input-text
                    required
                />
            </div>
            <div class="input-text mt-5">
                <input
                    class="input-text-input input-text-input-large"
                    type="text"
                    name="phone"
                    v-model="model.phone"
                    :placeholder="t('THP_FORM_PHONE')"
                    v-input-text
                />
            </div>

            <div class="input-textarea mt-5">
                <textarea
                    class="input-text-textarea input-text-textarea-large"
                    name="message"
                    rows="6"
                    v-model="model.message"
                    :placeholder="t('THP_FORM_MESSAGE')"
                ></textarea>
            </div>
        </div>
        <div class="mt-25 text-right">
            <div
                class="sourcing-form-submit-cell"
                :class="{ 'loading-animation': isSending }"
            >
                <input
                    type="submit"
                    class="btn-label btn-large"
                    :disabled="isPristine || isInvalid || sent || isSending"
                    :value="t('THP_FORM_SUBMIT')"
                />
            </div>
        </div>
    </form>
</template>
